import { QueryClient } from "@tanstack/react-query";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { sourcesService } from "@/services/sourcesService";
import { getSpaceIdsFromKbItem, getTopicIdsFromKbItem } from "@/lib/kbDocumentLinks";
import type { Source } from "@shared/schema";

export type LibraryDndItemType = "note" | "reference";

function invalidateDocumentQueries(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] }),
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/sources"] }),
    queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] }),
  ]);
}

export async function moveLibraryDocumentToFolder(
  queryClient: QueryClient,
  documentId: string,
  folderId: string | null,
  itemType: LibraryDndItemType
): Promise<void> {
  if (itemType === "note") {
    await knowledgeBaseService.updateKnowledgeBaseItem(documentId, { folderId });
  } else {
    await sourcesService.updateSource(documentId, { folderId } as any);
  }
  await invalidateDocumentQueries(queryClient);
}

function getSourceLinkedSpaceIds(source: Source): string[] {
  const anyS = source as Source & { linkedSpaceIds?: string[] };
  if (Array.isArray(anyS.linkedSpaceIds) && anyS.linkedSpaceIds.length > 0) {
    return [...anyS.linkedSpaceIds];
  }
  return [];
}

function getSourceLinkedTopicIds(source: Source): string[] {
  const anyS = source as Source & { linkedTopicIds?: string[]; topics?: string[] };
  if (Array.isArray(anyS.linkedTopicIds) && anyS.linkedTopicIds.length > 0) {
    return [...anyS.linkedTopicIds];
  }
  if (Array.isArray(anyS.topics)) {
    return [...anyS.topics];
  }
  return [];
}

export async function linkLibraryDocumentToSpace(
  queryClient: QueryClient,
  documentId: string,
  itemType: LibraryDndItemType,
  spaceId: string
): Promise<void> {
  if (itemType === "note") {
    const existing = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
    if (!existing) throw new Error("Document not found");
    const merged = Array.from(new Set([...getSpaceIdsFromKbItem(existing), spaceId]));
    await knowledgeBaseService.updateKnowledgeBaseItem(documentId, { spaceIds: merged });
  } else {
    const source = await sourcesService.getSource(documentId);
    if (!source) throw new Error("Reference not found");
    const merged = Array.from(new Set([...getSourceLinkedSpaceIds(source), spaceId]));
    await sourcesService.updateSource(documentId, { linkedSpaceIds: merged } as Parameters<typeof sourcesService.updateSource>[1]);
  }
  await invalidateDocumentQueries(queryClient);
}

export async function linkLibraryDocumentToTopic(
  queryClient: QueryClient,
  documentId: string,
  itemType: LibraryDndItemType,
  topicId: string,
  topicHomeSpaceId: string | null | undefined
): Promise<void> {
  if (itemType === "note") {
    const existing = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
    if (!existing) throw new Error("Document not found");
    const nextTopics = Array.from(new Set([...getTopicIdsFromKbItem(existing), topicId]));
    let nextSpaces = getSpaceIdsFromKbItem(existing);
    if (topicHomeSpaceId) {
      nextSpaces = Array.from(new Set([...nextSpaces, topicHomeSpaceId]));
    }
    await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
      topicIds: nextTopics,
      spaceIds: nextSpaces,
    });
  } else {
    const source = await sourcesService.getSource(documentId);
    if (!source) throw new Error("Reference not found");
    const mergedTopics = Array.from(new Set([...getSourceLinkedTopicIds(source), topicId]));
    const mergedSpaces = topicHomeSpaceId
      ? Array.from(new Set([...getSourceLinkedSpaceIds(source), topicHomeSpaceId]))
      : getSourceLinkedSpaceIds(source);
    await sourcesService.updateSource(documentId, {
      linkedTopicIds: mergedTopics,
      ...(topicHomeSpaceId ? { linkedSpaceIds: mergedSpaces } : {}),
    } as Parameters<typeof sourcesService.updateSource>[1]);
  }
  await invalidateDocumentQueries(queryClient);
}

export type LibraryDocumentDropHooks = {
  onOptimisticMove?: (itemId: string) => void;
  onClearOptimisticMove?: (itemId: string) => void;
};
